import React from 'react';
import { useTranslation } from 'react-i18next';
import { Drawer, Form, Button, SelectPicker, Message } from 'rsuite';

interface User {
  _id: string;
  firstname: string;
  lastname: string;
  registrationNumber: string;
  forgotten?: boolean;
}

interface DrawerViewProps {
  open: boolean;
  onClose: () => void;
  onFiltersChange: (filters: any) => void;
  onResetFilters: () => void;
  currentFilters: {
    userId: string;
    status: string;
    validatorId: string;
  };
  users: User[];
  usersLoading: boolean;
  toaster: any;
}

const DrawerView: React.FC<DrawerViewProps> = ({
  open,
  onClose,
  onFiltersChange,
  onResetFilters,
  currentFilters,
  users,
  usersLoading,
  toaster
}) => {
  const [formValue, setFormValue] = React.useState(currentFilters);
  const { t } = useTranslation();

  const statusOptions = [
    { label: t('all'), value: '' },
    { label: t('pending'), value: 'pending' },
    { label: t('accepted'), value: 'accepted' },
    { label: t('Reject'), value: 'Reject' }
  ];

  const userOptions = React.useMemo(() => {
    return [
      { label: t('ALL'), value: '' },
      ...users
        .filter(user => user.forgotten !== true) // Exclude forgotten users
        .map(user => ({
          label: `${user.firstname} ${user.lastname} (${user.registrationNumber})`,
          value: user._id
        }))
    ];
  }, [users, t]);

  const handleSubmit = () => {
    const hasFilters = Object.values(formValue).some(value => value !== '' && value !== null);

    if (hasFilters) {
      onFiltersChange({
        userId: formValue.userId,
        status: formValue.status,
        validatorId: formValue.validatorId
      });
      toaster.push(
        <Message type="success" duration={3000}>
          {t('Filters applied successfully!')}
        </Message>,
        { placement: 'topCenter' }
      );
    } else {
      onResetFilters();
    }
    onClose();
  };

  const handleReset = () => {
    setFormValue({
      userId: '',
      status: '',
      validatorId: ''
    });
    onResetFilters();
    toaster.push(
      <Message type="info" duration={3000}>
        {t('Filters have been reset!')}
      </Message>,
      { placement: 'topCenter' }
    );
    onClose();
  };

  React.useEffect(() => {
    if (open) {
      setFormValue(currentFilters);
    }
  }, [open, currentFilters]);

  return (
    <Drawer backdrop="static" size="sm" placement="right" onClose={onClose} open={open}>
      <Drawer.Header>
        <Drawer.Title>{t('Filter Options')}</Drawer.Title>
        <Drawer.Actions>
          <Button onClick={handleReset} appearance="subtle">
            {t('Reset')}
          </Button>
          <Button onClick={handleSubmit} appearance="primary">
            {t('Apply')}
          </Button>
          <Button onClick={onClose} appearance="subtle">
            {t('Cancel')}
          </Button>
        </Drawer.Actions>
      </Drawer.Header>

      <Drawer.Body>
        <Form fluid formValue={formValue} onChange={setFormValue}>
          <Form.Group>
            <Form.ControlLabel>{t('User & Registration Number')}</Form.ControlLabel>
            <Form.Control
              name="userId"
              accepter={SelectPicker}
              data={userOptions}
              loading={usersLoading}
              searchable
              placeholder={t('Select user')}
              block
            />
          </Form.Group>

          <Form.Group>
            <Form.ControlLabel>{t('Validator')}</Form.ControlLabel>
            <Form.Control
              name="validatorId"
              accepter={SelectPicker}
              data={userOptions}
              loading={usersLoading}
              searchable
              placeholder={t('Select validator')}
              block
            />
          </Form.Group>

          <Form.Group>
            <Form.ControlLabel>{t('Status')}</Form.ControlLabel>
            <Form.Control
              name="status"
              accepter={SelectPicker}
              data={statusOptions}
              searchable={false}
              placeholder={t('Select status')}
              block
            />
          </Form.Group>
        </Form>
      </Drawer.Body>
    </Drawer>
  );
};

export default DrawerView;
